import { useRef, Fragment, memo } from 'react'
import { ContentSource, Stream, Feed } from 'interfaces'
import { instanceOfStream } from 'services/content/util'
import { NavLink } from 'react-router'
import FeedIcon from 'components/FeedIcon'
import StreamFeedsNavigation from 'components/StreamView/StreamFeedsNavigation'
import SaveButton from 'components/SaveButton'
import Tooltip from '@mui/material/Tooltip'
import IconPrivate from '@mui/icons-material/VisibilityOffOutlined'
import { FormattedMessage } from 'react-intl'
import ContentTypeFilter from 'components/ContentTypeFilter'
import styles from './ContentLayoutHeader.pcss'
import { ContentType, FilterType } from 'shared'

export interface ContentLayoutHeaderProps {
  title: string
  source: ContentSource
  browseLinkText: string
  backLinkText: string
  browseUrl?: string
  filter: FilterType
  sourceSaved: boolean
  contentTypes: ContentType[]
  expanded: boolean
  saveLoading: boolean
  prefix?: string
  hideNavLink?: boolean
  onExpandLinkClick: () => void
  onBackLinkClick: () => void
  onFilterChanged: (filter: FilterType) => void
  onSave: () => void
}

export function ContentLayoutHeader(props: ContentLayoutHeaderProps) {
  const {
    onExpandLinkClick,
    browseLinkText,
    backLinkText,
    filter,
    contentTypes,
    onFilterChanged,
    title,
    expanded,
    onBackLinkClick,
    browseUrl,
    sourceSaved,
    source,
    saveLoading,
    onSave,
    hideNavLink,
    prefix
  } = props

  // keeps initial state of the "Save" button
  const initialSourceSavedRef = useRef<boolean>(sourceSaved)

  const typeFilter = expanded || contentTypes.length === 0
    ? null
    : (
      <ContentTypeFilter
        all={false}
        options={contentTypes}
        selected={filter}
        onFilterSelected={onFilterChanged}
      />
    )
  const navUrl = browseUrl || '/'
  const isStream = instanceOfStream(source)

  const navLink = expanded
    ? <a onClick={onBackLinkClick} className={styles.navigation}>{backLinkText}</a>
    : <NavLink to={navUrl} onClick={onExpandLinkClick} className={styles.navigation}>{browseLinkText}</NavLink>

  const avatar = isStream
    ? null
    : <FeedIcon feed={source as Feed} className={styles.avatar} />

  const expandedClass = expanded ? styles.expanded : ''
  const feeds = isStream && expanded
    ? <StreamFeedsNavigation stream={source as Stream} prefix={prefix || '/content'} />
    : null

  const privateIcon = isStream && (source as Stream).isPrivate && (
    <Tooltip placement="top" title={<FormattedMessage id="find.my-streams.labels.private-stream" />}>
      <IconPrivate className={styles['icon-private']} />
    </Tooltip>
  )

  // const lockedIcon = isStream && (source as Stream).originalId && (
  //   <Tooltip placement="top" title={<FormattedMessage id="find.my-streams.labels.unowned-stream" />}>
  //     <img src="/static/img/pp-logo-green.svg" width="20" />
  //   </Tooltip>
  // )

  return (
    <Fragment>
      <header className={`${styles.header} ${expandedClass}`} data-testid="content-layout-header">
        {
          // NOTE: We want to show Save button always for feeds, but for streams - only if not saved
          (!initialSourceSavedRef.current || !isStream) && (
            <SaveButton
              loading={saveLoading}
              onClick={onSave}
              saved={isStream ? sourceSaved : undefined}
              className={styles['btn-save']}
            />
          )
        }
        {avatar}
        {privateIcon}
        {/* {lockedIcon} */}
        <h2 className={`${styles.title} text-ellipsis`} title={title}>{title}</h2>
        {!hideNavLink && navLink}
        {typeFilter}
      </header>
      {feeds}
    </Fragment>
  )
}

export default memo(ContentLayoutHeader)
